// Wrapper around `fetch` that retries transient failures with exponential backoff.
import { isAbortError, throwIfAborted, waitForRetry } from '../utils/fetchCancellation';

export interface FetchRetryPolicy {
  httpStatuses: readonly number[];
  onRetry?: (retry: { attempt: number; delayMs: number; status?: number }) => void;
}

const DEFAULT_RETRIES = 3;
const DEFAULT_BASE_DELAY = 1000;
const MAX_DELAY = 8000;

// `attempt` is 0-indexed: 0 = the wait after the first try, 1 = after the second, etc.
function getBackoffDelay(attempt: number, baseDelay: number): number {
  const exponentialCeiling = Math.min(MAX_DELAY, baseDelay * 2 ** attempt);
  return Math.random() * exponentialCeiling;
}

function getRetryAfter(response: Response): number | undefined {
  const value = response.headers.get('Retry-After')?.trim();
  if (!value) return;
  if (/^\d+$/.test(value)) return Number(value) * 1000;
  // Numeric but invalid delta-seconds must not be interpreted as dates.
  if (!/GMT$/i.test(value)) return;
  const date = Date.parse(value);
  if (Number.isFinite(date)) return Math.max(0, date - Date.now());
}

async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  retries = DEFAULT_RETRIES,
  baseDelay = DEFAULT_BASE_DELAY,
  policy?: FetchRetryPolicy,
): Promise<Response> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    throwIfAborted(options.signal);
    let response: Response | undefined;
    try {
      response = await fetch(url, options);
    } catch (error) {
      throwIfAborted(options.signal);
      if (isAbortError(error)) throw error;
      lastError = error;
      if (attempt >= retries) throw lastError;
    }
    throwIfAborted(options.signal);
    // HTTP retries are opt-in, so unrelated writes keep their existing policy.
    if (response && (!policy?.httpStatuses.includes(response.status) || attempt >= retries)) return response;
    const retryAfter = response ? getRetryAfter(response) : undefined;
    // Do not shorten a server-requested wait that exceeds our recovery limit.
    if (response && retryAfter !== undefined && retryAfter > MAX_DELAY) return response;
    const delayMs = Math.max(getBackoffDelay(attempt, baseDelay), retryAfter ?? 0);
    policy?.onRetry?.({ attempt: attempt + 1, delayMs, ...(response && { status: response.status }) });
    // Release an unused error response before sending the next request.
    void response?.body?.cancel().catch(() => undefined);
    await waitForRetry(delayMs, options.signal);
  }
  // Unreachable in practice (the loop either returns or throws), but keeps TS happy.
  throw lastError;
}

export default fetchWithRetry;
